import { saveAs } from 'file-saver';

export type XmlMaterial = {
  ArticleNo: string;
  NetQuantity: number | string;
  GrossQuantity?: number | string;
  Localization?: string;
  ParentImportMaterialId?: string | null;
  ParentQuantityFactor?: number | string | null;
};

export type XmlRoof = {
  Name: string;
  NetRoofSqm?: number | string;
  Surface?: number | string;
  ParapetLengthLm?: number | string;
  ParapetSurface?: number | string;
  ParapetHeight?: number | string;
  Roofing?: number | string;
  VerticalAreaLength?: number | string;
  CombustibleInsulationSqm?: number | string;
  CombustibleInsulationAvgThickness?: number | string;
  SlopeTypeEPS?: number | string;
  EpsKileLm?: number | string;
  MwKileLm?: number | string;
  NonCombustibleInsulationSqm?: number | string;
  NonCombustibleInsulationAvgThickness?: number | string;
  SlopeTypeMw?: number | string;
  NonCombustibleInsulationFireSecSqm?: number | string;
  NonCombustibleInsulationFireSecAvgThickness?: number | string;
  VerticalAreaSqm?: number | string;
  CornersNum?: number | string;
  TapeDiffusionBarrier?: number | string;
  Materials: XmlMaterial[];
};

export function exportRoofsToXml(roofItems: XmlRoof[], filename: string) {
  const xml = buildRoofsXml(roofItems);
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8;' });
  saveAs(blob, filename);
}

export function buildRoofsXml(roofItems: XmlRoof[]): string {
  const header = '<?xml version="1.0" encoding="UTF-8"?>';
  const roofsXml = roofItems.map((roof) => buildRoofXml(roof)).join('');
  return `${header}<Roofs>${roofsXml}</Roofs>`;
}

function roundUpToTwoDecimals(value: number): number {
  return Math.ceil(value * 100) / 100;
}

function isNumericString(value: string): boolean {
  if (value == null) return false;
  const trimmed = value.trim();
  if (trimmed === '') return false;
  return !isNaN(Number(trimmed));
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function element(tag: string, value: unknown | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';

  let str: string;
  if (typeof value === 'number') {
    const rounded = roundUpToTwoDecimals(value);
    str = String(rounded);
  } else if (typeof value === 'string' && isNumericString(value)) {
    const rounded = roundUpToTwoDecimals(Number(value));
    str = String(rounded);
  } else {
    str = String(value);
  }

  return `<${tag}>${xmlEscape(str)}</${tag}>`;
}

function buildRoofXml(roof: XmlRoof): string {
  const parts: string[] = [];
  parts.push('<Roof>');
  parts.push(element('Name', roof.Name));
  parts.push(element('NetRoofSqm', roof.NetRoofSqm));
  parts.push(element('Surface', roof.Surface));
  parts.push(element('ParapetLengthLm', roof.ParapetLengthLm));
  parts.push(element('ParapetSurface', roof.ParapetSurface));
  parts.push(element('ParapetHeight', roof.ParapetHeight));
  parts.push(element('Roofing', roof.Roofing));
  parts.push(element('VerticalAreaLength', roof.VerticalAreaLength));
  parts.push(
    element('CombustibleInsulationSqm', roof.CombustibleInsulationSqm)
  );
  parts.push(
    element(
      'CombustibleInsulationAvgThickness',
      roof.CombustibleInsulationAvgThickness
    )
  );
  parts.push(element('SlopeTypeEPS', roof.SlopeTypeEPS));
  parts.push(element('EpsKileLm', roof.EpsKileLm));
  parts.push(element('MwKileLm', roof.MwKileLm));
  parts.push(
    element('NonCombustibleInsulationSqm', roof.NonCombustibleInsulationSqm)
  );
  parts.push(
    element(
      'NonCombustibleInsulationAvgThickness',
      roof.NonCombustibleInsulationAvgThickness
    )
  );
  parts.push(element('SlopeTypeMw', roof.SlopeTypeMw));
  parts.push(
    element(
      'NonCombustibleInsulationFireSecSqm',
      roof.NonCombustibleInsulationFireSecSqm
    )
  );
  parts.push(
    element(
      'NonCombustibleInsulationFireSecAvgThickness',
      roof.NonCombustibleInsulationFireSecAvgThickness
    )
  );
  parts.push(element('VerticalAreaSqm', roof.VerticalAreaSqm));
  parts.push(element('CornersNum', roof.CornersNum));
  parts.push(element('TapeDiffusionBarrier', roof.TapeDiffusionBarrier));

  // Materials
  parts.push('<Materials>');
  for (const mat of roof.Materials) {
    parts.push('<Material>');
    parts.push(element('ArticleNo', mat.ArticleNo));
    parts.push(element('NetQuantity', mat.NetQuantity));
    parts.push(element('GrossQuantity', mat.GrossQuantity));
    parts.push(element('Localization', mat.Localization));
    parts.push('</Material>');
  }
  parts.push('</Materials>');

  parts.push('</Roof>');
  return parts.join('');
}
